import { lmaReduxStore } from '@redux/store';
import { IPaketshopReprocessSummary, IPaketshopVisit, PaketshopVisitStatus } from '../interfaces/IPaketshopVisit';

/**
 * Same-origin path proxied by nginx to the internal lma-processors service (see docker/nginx.vh.default.conf).
 * Using same-origin avoids CORS issues and avoids exposing internal processors through a public ingress.
 */
const PAKETSHOP_API_BASE = '/paketshop-api';

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  try {
    const token = lmaReduxStore?.getState()?.auth?.tokenId;
    if (token) {
      headers['token'] = token;
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    // Ignore store access errors in test environments
  }
  return headers;
}

/**
 * Fetches the daily Paketshop visit facts for a given date and optional depot.
 *
 * @param date ISO string YYYY-MM-DD
 * @param depotId optional Hermes Depot identifier (e.g. '01')
 */
export async function fetchPaketshopVisits(date?: string, depotId?: string): Promise<IPaketshopVisit[]> {
  const params = new URLSearchParams();
  if (date) {
    params.set('date', date);
  }
  if (depotId && depotId !== 'ALL') {
    params.set('depotId', depotId);
  }

  const query = params.toString();
  const url = `${PAKETSHOP_API_BASE}/visits${query ? `?${query}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Paketshop Visits API error: ${response.status} ${response.statusText}`);
  }

  const rawData: any[] = await response.json();
  return (rawData || []).map((item) => {
    const rawStatus = item.visitStatus || item.visitStatusLabel;
    let resolvedStatus: PaketshopVisitStatus = 'KEINE_ANFAHRT';
    if (rawStatus === 'SERVICED' || item.isServiced === true) {
      resolvedStatus = 'SERVICED';
    } else if (rawStatus === 'GESCHLOSSEN') {
      resolvedStatus = 'GESCHLOSSEN';
    }

    return {
      ...item,
      visitStatus: resolvedStatus,
      visitStatusLabel: resolvedStatus,
      isServiced: resolvedStatus === 'SERVICED',
      servicingTours: Array.isArray(item.servicingTours)
        ? item.servicingTours
        : item.servicingTours
        ? [String(item.servicingTours)]
        : [],
    };
  });
}

/**
 * Fetches the historical daily visit records for a single Paketshop across dates.
 *
 * @param shopId Paketshop internal ID
 */
export async function fetchPaketshopVisitsByShop(shopId: number | string): Promise<IPaketshopVisit[]> {
  const url = `${PAKETSHOP_API_BASE}/shop/${encodeURIComponent(String(shopId))}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Paketshop Visits API error: ${response.status} ${response.statusText}`);
  }

  const rawData: any[] = await response.json();
  return (rawData || []).map((item) => {
    const rawStatus = item.visitStatus || item.visitStatusLabel;
    let resolvedStatus: PaketshopVisitStatus = 'KEINE_ANFAHRT';
    if (rawStatus === 'SERVICED' || item.isServiced === true) {
      resolvedStatus = 'SERVICED';
    } else if (rawStatus === 'GESCHLOSSEN') {
      resolvedStatus = 'GESCHLOSSEN';
    }

    return {
      ...item,
      visitStatus: resolvedStatus,
      visitStatusLabel: resolvedStatus,
      isServiced: resolvedStatus === 'SERVICED',
      servicingTours: Array.isArray(item.servicingTours)
        ? item.servicingTours
        : item.servicingTours
        ? [String(item.servicingTours)]
        : [],
    };
  });
}

/**
 * Triggers reprocess / replay calculation for a selected date.
 * Re-reads stop events and master data, re-aggregates multi-stops, and persists facts to Cassandra.
 *
 * @param date ISO string YYYY-MM-DD
 */
export async function reprocessPaketshopVisits(date: string): Promise<IPaketshopReprocessSummary> {
  const params = new URLSearchParams({ date });
  const url = `${PAKETSHOP_API_BASE}/reprocess?${params.toString()}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Paketshop Visits Replay error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}
